import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { RBACService } from '../shared/rbac.service';
import { UtilsService } from '../shared/utils.service';
import { AuditItemDataSource } from './audit-item.data.source';
import { AuditItemModel } from './audit-item.model';
import { AuditItemsService } from './audit-items.service';

@Component({
  selector: 'app-audit-items',
  templateUrl: './audit-items.component.html',
  styleUrls: ['./audit-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AuditItemsComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() auditId: number;
  @Input() template = true;
  dataSource = new AuditItemDataSource(this.app, this.api);
  displayedColumns = ['name', 'section', 'status', 'note', 'buttons'];
  sections: string[];
  selectedSection: string;
  query: string;

  constructor(
    private auditItemService: AuditItemsService,
    private utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // RBAC: In standalone mode (no auditId), enforce RBAC using F_REGISTERS_INSPECTIONS_AUDITS.
    // In embedded mode (has auditId), skip RBAC: parent component governs access.
    if (!this.auditId) {
      // Standalone mode: resolve RBAC permission flags.
      this.canView = this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
      this.canEdit = this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      );
      this.canDelete = this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      );

      // Redirect if user has NO permissions at all.
      if (!this.canView && !this.canEdit && !this.canDelete) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to access inspection/audit questions.'
        );
        this.router.navigate(['/dashboard']);
        return;
      }
    } else {
      // Embedded mode: skip RBAC, parent component governs access.
      this.canView = true;
      this.canEdit = true;
      this.canDelete = true;
    }

    this.dataSource.template = this.template;
    this.dataSource.auditId = this.auditId;

    // Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
      this.auditItemService
        .getSections()
        .subscribe((response) => (this.sections = response.data));
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;
      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  filterSection() {
    this.dataSource.getDataForSection(false, this.selectedSection);
  }

  delete(auditItem: AuditItemModel) {
    // Layer 2 guard: check canDelete before proceeding.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspection/audit questions.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Question',
      `Are you sure you want to remove the "${auditItem.name}" question?`,
      () => {
        this.auditItemService.remove([auditItem.id]).subscribe(() => {
          this.utils.showToast(`The "${auditItem.name}" question was removed.`);
          this.dataSource.getData(false);
        });
      }
    );
  }
}
